import { endSession, isSessionEnding } from '@lib';
import { hasToken, isTokenExpired, millisecondsUntilExpiry } from '@utils';
import { useEffect, useRef } from 'react';

/** setTimeout fires immediately past this, so a longer wait is taken in stages. */
const MAX_TIMER_DELAY = 2 ** 31 - 1;

/**
 * Watches the signed-in session and ends it the moment it runs out, rather than leaving the
 * user on a page whose next request is going to fail.
 *
 * Returns true once the session is over and something is already on its way to the login
 * screen, so the caller can hold the screen instead of drawing a page with nothing behind it.
 */
export const useSessionExpiry = (): boolean => {
  // Whether this tab ever held a token. A token that goes missing is a session that ended: the
  // cookie carrying it expires on its own, so the tab left open overnight wakes up with no
  // token rather than with a stale one. Never having had one is another matter entirely - that
  // is a visitor who is simply not signed in, and they get the login screen without being told
  // anything expired.
  const hadToken = useRef(hasToken());

  // Read on every render rather than only in the effect below: the route guard needs the
  // answer before it decides what to show, and an effect would land a paint too late.
  const ended = hadToken.current && isTokenExpired();

  useEffect(() => {
    if (ended) endSession('expired');
  }, [ended]);

  useEffect(() => {
    if (!hadToken.current) return undefined;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = () => {
      if (timer) clearTimeout(timer);

      // Covers both ways a session ends: the expiry passing, and the token disappearing with
      // the cookie that held it.
      if (isTokenExpired()) {
        endSession('expired');
        return;
      }

      const remaining = millisecondsUntilExpiry();
      if (remaining === undefined) return; // a token with no expiry: no clock to set

      // A second past the expiry, so a timer that fires a hair early reschedules itself
      // instead of ending a session that is still good.
      timer = setTimeout(check, Math.min(remaining + 1000, MAX_TIMER_DELAY));
    };

    // Timers in a background tab are throttled and a sleeping machine stops them altogether,
    // so the clock is read again whenever the tab comes back to the user.
    const recheck = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', check);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', check);
    };
  }, []);

  return ended || isSessionEnding();
};
